// Soft-delete confirm modal for a manufacturing model (recipe).
// Hard delete is intentionally absent — recipes may be referenced by
// BOM lines and deal links; deactivation hides the model from pickers.
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  ghostBtnClass,
  mfgConfirmDialogClass,
  mfgSolidDangerBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingModelDeleteConfirm({
  open,
  recipe,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  recipe: MfgRecipeSummary | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open || !recipe) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfg-model-delete-title"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className={`max-w-md ${mfgConfirmDialogClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="mfg-model-delete-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-100"
        >
          Deactivate model {recipe.code} · {recipe.name}?
        </h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          This marks the model inactive — it stays in the database but is hidden from
          pickers and new BOM lines. If other recipes or deals still reference it, those
          links remain until you reassign them. This action requires org admin or owner
          confirmation.
        </p>
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={ghostBtnClass} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={mfgSolidDangerBtnClass}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? "Deactivating…" : "Deactivate model"}
          </button>
        </div>
      </div>
    </div>
  );
}
